// CATÁLOGO v2 — CONGELADO. NO SE TOCA NUNCA MÁS.
//
// 4 pilares / 21 conceptos / 60 subconceptos. Es el catálogo con el que se
// puntuó `EV-2026-0001` y cualquier evaluación anterior al v3.
//
// La regla que hace que este fichero exista: una evaluación se lee SIEMPRE con
// su `catalog_version` guardado, nunca con el catálogo vigente. Editar aquí una
// etiqueta, quitar un subconcepto o cambiar una clave no "corrige" nada: cambia
// lo que dice un informe YA FIRMADO, que se puntuó contra esta lista y no
// contra otra. Si el contenido tiene que cambiar, nace una versión nueva.
//
// Las evaluaciones v2 no se migran ni se recalculan, y sus medias NO son
// comparables con las del v3: técnico mide otra cosa.

import type { Catalogo, PillarDef } from './evaluationCatalog';

// ── Catálogo (claves estables; etiquetas de categoría EN, de subcategoría ES) ──
const PILARES_V2: PillarDef[] = [
  {
    key: 'tecnico',
    label: 'Técnico',
    color: '#2196F3',
    categories: [
      {
        key: 'short_passing',
        label: 'Short passing',
        subs: [
          { key: 'precision', label: 'Precisión' },
          { key: 'weight', label: 'Peso del pase' },
          { key: 'under_pressure', label: 'Bajo presión' },
        ],
      },
      {
        key: 'long_passing',
        label: 'Long passing',
        subs: [
          { key: 'precision', label: 'Precisión' },
          { key: 'technique', label: 'Técnica (raso-bombeado)' },
          { key: 'switch_play', label: 'Cambio de orientación' },
        ],
      },
      {
        key: 'receiving',
        label: 'Receiving skills',
        subs: [
          { key: 'first_touch', label: 'Primer toque' },
          { key: 'control_orientation', label: 'Orientación del control' },
          { key: 'under_pressure', label: 'Bajo presión' },
        ],
      },
      {
        key: 'ball_carrying',
        label: 'Ball carrying',
        subs: [
          { key: 'at_speed', label: 'Conducción en velocidad' },
          { key: 'tight_space', label: 'Espacio reducido' },
          { key: 'protection', label: 'Protección' },
        ],
      },
      {
        key: 'ball_mastery',
        label: 'Ball mastery',
        subs: [
          { key: 'dribbling_1v1', label: 'Regate 1v1' },
          { key: 'both_feet', label: 'Ambas piernas' },
        ],
      },
      {
        key: 'end_product',
        label: 'End product',
        subs: [
          { key: 'finishing', label: 'Finalización' },
          { key: 'final_pass', label: 'Último pase-asistencia' },
          { key: 'decision_final_third', label: 'Decisión en zona de definición' },
        ],
      },
    ],
  },
  {
    key: 'tactico',
    label: 'Táctico',
    color: '#4CAF50',
    categories: [
      {
        key: 'in_possession',
        label: 'In possession',
        subs: [
          { key: 'support', label: 'Apoyos' },
          { key: 'passing_lines', label: 'Líneas de pase' },
          { key: 'space_occupation', label: 'Ocupación de espacios' },
        ],
      },
      {
        key: 'out_of_possession',
        label: 'Out of possession',
        subs: [
          { key: 'pressing', label: 'Presión' },
          { key: 'cover', label: 'Coberturas' },
          { key: 'recovery', label: 'Recuperación' },
        ],
      },
      {
        key: 'transitions',
        label: 'Transitions',
        subs: [
          { key: 'after_loss', label: 'Tras pérdida' },
          { key: 'after_recovery', label: 'Tras recuperación' },
          { key: 'transition_speed', label: 'Velocidad de transición' },
        ],
      },
      {
        key: 'positioning',
        label: 'Positioning',
        subs: [
          { key: 'by_phase', label: 'Según fase' },
          { key: 'by_opponent', label: 'Según rival' },
          { key: 'by_ball', label: 'Según balón' },
        ],
      },
      {
        key: 'game_awareness',
        label: 'Game awareness',
        subs: [
          { key: 'game_reading', label: 'Lectura del juego' },
          { key: 'anticipation', label: 'Anticipación' },
          { key: 'scanning', label: 'Escaneo' },
        ],
      },
      {
        key: 'movement',
        label: 'Movement',
        subs: [
          { key: 'runs', label: 'Desmarques' },
          { key: 'mobility', label: 'Movilidad' },
          { key: 'timing', label: 'Temporización' },
        ],
      },
      {
        key: 'decision_making',
        label: 'Decision-making',
        subs: [
          { key: 'action_selection', label: 'Selección de la acción' },
          { key: 'speed', label: 'Velocidad' },
          { key: 'under_pressure', label: 'Bajo presión' },
        ],
      },
    ],
  },
  {
    key: 'fisico',
    label: 'Físico',
    color: '#FF5722',
    categories: [
      {
        key: 'speed',
        label: 'Speed',
        subs: [
          { key: 'acceleration', label: 'Aceleración' },
          { key: 'top_speed', label: 'Velocidad máxima' },
          { key: 'with_ball', label: 'Velocidad con balón' },
        ],
      },
      {
        key: 'strength',
        label: 'Strength',
        subs: [
          { key: 'duels', label: 'Duelos' },
          { key: 'lower_body', label: 'Tren inferior' },
          { key: 'core_stability', label: 'Core-estabilidad' },
        ],
      },
      {
        key: 'stamina',
        label: 'Stamina',
        subs: [
          { key: 'aerobic', label: 'Resistencia aeróbica' },
          { key: 'repeat_efforts', label: 'Repetir esfuerzos' },
        ],
      },
      {
        key: 'abc',
        label: 'A-B-C',
        subs: [
          { key: 'agility', label: 'Agilidad' },
          { key: 'balance', label: 'Equilibrio' },
        ],
      },
    ],
  },
  {
    key: 'actitud',
    label: 'Mental / Actitud',
    color: '#FFF200',
    categories: [
      {
        key: 'communication',
        label: 'Communication',
        subs: [
          { key: 'with_teammates', label: 'Con compañeros' },
          { key: 'with_staff', label: 'Con cuerpo técnico' },
          { key: 'leadership', label: 'Liderazgo' },
        ],
      },
      {
        key: 'confidence',
        label: 'Confidence',
        subs: [
          { key: 'on_ball', label: 'Con balón' },
          { key: 'off_ball', label: 'Sin balón' },
          { key: 'after_mistake', label: 'Tras error' },
        ],
      },
      {
        key: 'concentration',
        label: 'Concentration',
        subs: [
          { key: 'focus', label: 'Mantener foco' },
          { key: 'key_moments', label: 'Momentos clave' },
          { key: 'duration', label: 'Duración' },
        ],
      },
      {
        key: 'professionalism',
        label: 'Professionalism',
        subs: [
          { key: 'training_attitude', label: 'Actitud en entreno' },
          { key: 'commitment', label: 'Compromiso-puntualidad' },
          { key: 'habits', label: 'Hábitos' },
        ],
      },
    ],
  },
];

export const CATALOGO_V2: Catalogo = { version: 2, pilares: PILARES_V2 };
